import {
    IActiveTranslation,
    IAgent,
    IInteraction,
    IInteractionDetailsResponse,
    IInteractionMessage,
    IPhraseTriggerResult,
    IScorecardEvaluationSummary,
    ITag,
    ITagGroup,
    IUser,
    IWritingIssue,
    Sentiment,
} from "@edgetier/watchtower-types";
import { createContext, useContext } from "react";
import { AxiosError } from "axios";
import { IAutoMenuElement } from "../../../auto-menu/auto-menu.types";
import { doNothing } from "@edgetier/utilities";

type DataError = AxiosError<any> | string | null;

interface IDataFetch<T> {
    data: T | null;
    isLoading: boolean;
    error?: DataError;
}
export interface IPaginatedDataFetch<T> {
    data: T[] | null;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    isLoadingNextPage: boolean;
    isLoadingPreviousPage: boolean;
    isLoading: boolean;
    error?: DataError;
    fetchNextPage?: () => void;
    fetchPreviousPage?: () => void;
}

export type IInteractionFromSameContactItem = Pick<IInteraction, "interactionId" | "startDateTime" | "interactionType">;

interface ISidebarSection {
    title: string;
    key: string;
    /**
     * Function to render the content of the sidebar section.
     * To avoid unnecessary re-computations, memoize the result using React.useMemo
     * in the consuming component, passing relevant dependencies.
     */
    content: (context: IInteractionModalContext) => React.ReactNode;
    isOpenByDefault?: boolean;
    isLoading?: boolean;
    header?: (context: IInteractionModalContext) => React.ReactNode;
    onToggleAccordion?: () => void;
}

interface IInteractionModalContext {
    user: IDataFetch<IUser>;
    interaction: IDataFetch<IInteraction>; // TBI
    interactionWritingIssues: IDataFetch<{ [x: string]: IWritingIssue[] }>; // TBI
    interactionMessages: IDataFetch<IInteractionMessage[]> & {
        highlightedMessageId: string | null;
        setHighlightedMessageId: (id: string | null) => void;
    }; // TBI
    interactionMessagesAgents: IDataFetch<IAgent[]>; // TBI
    interactionMessagesTagGroups: IDataFetch<ITagGroup[]>; // TBI
    interactionDetails: IDataFetch<IInteractionDetailsResponse>; // TBI
    interactionsFromSameContact?: IPaginatedDataFetch<IInteractionFromSameContactItem>;
    interactionsFromSameContactNavigation?: {
        onClick: (interactionId: IInteractionFromSameContactItem["interactionId"]) => void;
    };
    elements?: {
        audio?: {
            audioPlayer?: () => React.ReactElement;
        };
        sentiments?: {
            tooltipContent?: (props: {
                sentiments: Partial<Record<Sentiment, boolean>>;
                messageId: string;
            }) => React.ReactNode;
        };
        tags?: {
            tooltipContent?: (props: {
                tag: string;
                tagId: string;
                tagGroup: string;
                messageId: string;
                onMouseEnter?: (phraseTriggerDetails?: IPhraseTriggerResult) => void;
                onMouseLeave?: (phraseTriggerDetails?: IPhraseTriggerResult) => void;
            }) => React.ReactNode;
            editTagButton?: (props: { tagId: string; messageId: string }) => React.ReactNode;
            removeTagButton?: (props: { tag: ITag; messageId: string }) => React.ReactNode;
            addTagButton?: (props: { messageId: string }) => React.ReactNode;
        };
    };
    actions?: {
        moreActions?:
            | {
                  items: IAutoMenuElement[];
              }
            | { menu: React.ReactNode };
        customerOverview?: {
            onClick: () => Promise<void>;
            isDisabled: boolean;
        };
        writingIssues?: {
            onToggle: () => Promise<void>;
            isEnabled: boolean;
        };
        translation?: {
            onToggle: () => Promise<void>;
            toggleMessageTranslation: (
                interactionMessageId: string,
                text: string,
                languageIsoCode: string
            ) => Promise<void>;
            activeTranslations: Record<string, IActiveTranslation>;
            isEnabled: boolean;
            isDisabled: boolean;
        };
        sentiments?: {
            onToggle: (messageId: string, sentiment: Sentiment) => Promise<void>;
            isLoading: boolean;
        };
        tracking?: {
            trackUser: (interactionId: string) => void;
        };
    };
    getContactUrl?: (contactId: string) => Promise<string>; // TBI
    getViewReviewUrl?: (reviewSummary: IScorecardEvaluationSummary) => Promise<string>; // TBI
    onStartReview?: () => Promise<void>; // TBI
    onSentimentToggle?: (interactionMessage: IInteractionMessage, sentiment: Sentiment) => Promise<void>; // TBI
    interactionNavigation?: {
        currentIndex: number;
        total: number;
        showInteractionCount: boolean;
        isLoading: boolean;
        hasNext: boolean;
        onPrevious: () => void;
        onNext: () => void;
    };
    permissions: {
        interactionSummary: boolean;
        viewReviews: boolean;
        startReviews: boolean;
    };
    sidebar: {
        sections?: ISidebarSection[];
    };
}

const defaultInteractionModalContext: IInteractionModalContext = {
    user: {
        data: null,
        isLoading: false,
    },
    interaction: {
        data: null,
        isLoading: false,
    },
    interactionWritingIssues: {
        data: null,
        isLoading: false,
    },
    interactionMessages: {
        data: null,
        isLoading: false,
        highlightedMessageId: null,
        setHighlightedMessageId: doNothing,
    },
    interactionMessagesAgents: {
        data: null,
        isLoading: false,
    },
    interactionMessagesTagGroups: {
        data: null,
        isLoading: false,
    },
    interactionDetails: {
        data: null,
        isLoading: false,
    },
    interactionsFromSameContact: undefined,
    getContactUrl: async () => Promise.resolve("#"),
    getViewReviewUrl: async () => Promise.resolve("#"),
    onStartReview: async () => Promise.resolve(),
    onSentimentToggle: undefined,
    interactionNavigation: undefined,
    permissions: {
        interactionSummary: true,
        viewReviews: true,
        startReviews: true,
    },
    sidebar: {
        sections: [],
    },
};

export const InteractionModalContext = createContext<IInteractionModalContext>(defaultInteractionModalContext);

const useInteractionModalContext = () => {
    const context = useContext(InteractionModalContext);
    if (!context) {
        throw new Error("useInteractionModalContext must be used within an InteractionModalProvider");
    }
    return context;
};

const InteractionModalProvider = ({
    children,
    ...otherProps
}: Partial<IInteractionModalContext> & {
    children: React.ReactNode | ((context: IInteractionModalContext) => React.ReactNode);
}) => {
    // This provider would typically wrap children components and provide context values
    const contextValue = { ...defaultInteractionModalContext, ...otherProps };
    return (
        <InteractionModalContext.Provider value={contextValue}>
            {typeof children === "function" ? children(contextValue) : children}
        </InteractionModalContext.Provider>
    );
};

export { InteractionModalProvider, useInteractionModalContext };
